import React, { useState, useEffect, useContext, useRef } from 'react';
import { ICallInteraction, IDebitmanagment } from '../../../types/repossession.type';
import { InfoTable } from '../../../common/components/ui/table/InfoTable';
import GenericTable from '../../../common/components/ui/table/GenericTable';
import InputFormik from '../../../common/components/form/InputFormik';
import { Field, Formik } from 'formik';
import { Form } from 'react-router-dom';
import { useToastContext } from '../../../common/components/ui/toast/toast.context';
import ButtonWithLoader from '../../../common/components/ui/loading/ButtonLoading';
import { AuthContext } from '../../../common/components/auth/authContext/AuthContext';
import { getVehicleById } from '../../../common/services/vehicles.api';
import { createCallInteractions } from '../../../common/services/debit-management.api';
interface NegotiationModalProps {
  initialIsOpen?: boolean;
  onClose: () => void;
  data: IDebitmanagment;
  onUpdateInteraction?: (updatedItem: IDebitmanagment) => void;
}


const columnPayments = {
  description: (value: unknown) => {
    if (!value) return '';
    return String(value).replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();
  },

  dueDate: (value: unknown) => {
    if (!value) return '';
    try {
      const date = new Date(String(value));
      return new Intl.DateTimeFormat('pt-BR').format(date);
    } catch (error) {
      return String(value || '');
    }
  },

  individualAmountPayment: (value: unknown) => {
    if (value === null || value === undefined) return 'R$ 0,00';
    try {
      const amount = Number(value);
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(amount);
    } catch (error) {
      return `R$ ${String(value || 0)}`;
    }
  },
};


const HEADERS_NEGOTIATIONS: Record<string, string> = {
  description: 'Detalhamento do Débito',
  dueDate: 'Data de Vencimento',
  individualAmountPayment: 'Valor Individual',
};

const HEADERS_CALLINTERACTION: Record<string, string> = {
  author: 'Autor',
  contactDate: 'Data da Interação',
  notes: 'Observações',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TrafficFineSection = ({ title, data, className }: { title: string; data: { label: string; value: any }[]; className: string }) => (
  <section className={`tabs-item-style ${className}`}>
    <div>
      <InfoTable title={title} data={data} />
    </div>
  </section>
);

const columnCallInteractions = {
  contactDate: (value: unknown) => {
    if (!value) return '';
    try {
      const date = value instanceof Date ? value : new Date(String(value));
      return new Intl.DateTimeFormat('pt-BR').format(date);
    } catch (error) {
      return String(value || '');
    }
  },

  createdAt: (value: unknown) => {
    if (!value) return '';
    try {
      const date = value instanceof Date ? value : new Date(String(value));
      return new Intl.DateTimeFormat('pt-BR').format(date);
    } catch (error) {
      return String(value || '');
    }
  },

  author: (value: unknown) => String(value || ''),

  notes: (value: unknown) => String(value || '')
};


const PaymentArrangementModal: React.FC<NegotiationModalProps> = ({
  initialIsOpen = false,
  onClose,
  data,

  onUpdateInteraction
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(initialIsOpen);
  const [debitmanagmentItem, setDebitmanagmentItem] = useState<IDebitmanagment | null>(null);
  const [callInteractions, setCallInteractions] = useState<ICallInteraction[]>([]);
  const { notifySuccess, notifyError } = useToastContext()
  const [bindVelotrackInProgress, setBindVelotrackInProgress] = useState(false)
  const [namePartner, setNamePartner] = useState<string | null>(null);
  const { auth } = useContext(AuthContext)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formikRef = useRef<any>(null);

  const handleContactRegistration = async () => {
    const dateNow = new Date();
    setBindVelotrackInProgress(true);

    const formValues = formikRef.current?.values;
    const interactionDate = formValues?.birthDate;
    const observations = formValues?.notes;
    if (!interactionDate) {
      setBindVelotrackInProgress(false);

      return notifyError('Por favor, preencha a data de interação com o cliente');
    }

    if (!observations) {
      setBindVelotrackInProgress(false);
      return notifyError('Por favor, preencha o campo de observações');
    }

    const [year, month, day] = interactionDate.split('-').map(Number);
    const contactDate = new Date(year, month - 1, day);

    const newInteraction: ICallInteraction = {
      interactionId: auth?.user?.sub?.replace("auth0|", "") || '',
      contactDate: contactDate,
      notes: observations,
      author: auth?.user.name,
      createdAt: dateNow,
    };

    const insertResultCallInteraction = await createCallInteractions(
      debitmanagmentItem?._id || '',
      debitmanagmentItem?.negotiations?.[0]?._id || '',
      newInteraction
    );

    if (!insertResultCallInteraction.ok) {
      setBindVelotrackInProgress(false);
      return notifyError(insertResultCallInteraction.message || 'Erro ao registrar contato');
    }

    setCallInteractions(prevInteractions => [...prevInteractions, newInteraction]);

    if (debitmanagmentItem && onUpdateInteraction) {
      const updatedItem = JSON.parse(JSON.stringify(debitmanagmentItem)) as IDebitmanagment;

      if (!updatedItem.negotiations || !updatedItem.negotiations[0]) {
        updatedItem.negotiations = [{
          _id: debitmanagmentItem?.negotiations?.[0]?._id || '',
          total: debitmanagmentItem?.amount || 0,
          createdAt: new Date(),
          updatedAt: new Date(),
          paymentNegotiations: [],
          callCenterInteractions: [newInteraction]
        }];
      } else {
        if (!updatedItem.negotiations[0].callCenterInteractions) {
          updatedItem.negotiations[0].callCenterInteractions = [];
        }
        updatedItem.negotiations[0].callCenterInteractions.push(newInteraction);
      }
      formikRef.current?.resetForm();
      onUpdateInteraction(updatedItem);
    }

    setBindVelotrackInProgress(false);
    notifySuccess('Registro de contato realizado com sucesso!');
  };

  const fetchData = async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { _original } = data as any || {};
    const result = _original as IDebitmanagment;
    setIsOpen(initialIsOpen);
    setDebitmanagmentItem(result || null);
    setCallInteractions(result?.negotiations?.flatMap(n => n.callCenterInteractions || []).filter(Boolean) || []);

    if (result?.renter?.partnerId) {
      try {
        const resultVehicle = await getVehicleById(result.renter.vehicleId || '');

        if (!resultVehicle.ok) {
          setNamePartner(null);
          return notifyError(resultVehicle.message || 'Erro ao buscar veículo');
        }

        setNamePartner(resultVehicle.data?.partnerInfo.companyName || null);
      } catch (error) {
        setNamePartner(null);
        notifyError('Erro ao buscar veículo');
      }
    }
  };

  useEffect(() => {


    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialIsOpen, data]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        role="dialog"
        aria-modal="true"
        onClick={handleClose}
      >
        <div
          className="bg-white p-4 rounded-md shadow w-full max-w-[75%] max-h-[95vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="text-lg font-bold mb-3 sticky top-0 bg-white">Negociação</h2>

          <div className="border-t dark:border-gray-600 overflow-y-auto flex-1">
            <div className="overflow-y-auto">
              <TrafficFineSection
                title="Cliente"
                className="mt-3 mb-2"
                data={[
                  { label: "Nome", value: debitmanagmentItem?.renter?.name || 'N/A' },
                  { label: "CPF", value: debitmanagmentItem?.renter?.cpf || 'N/A' },
                  {
                    label: "Whatsapp",
                    value: debitmanagmentItem?.renter?.whatsapp ?
                      <a
                        className="text-[#802d2d] hover:underline"
                        href={`https://wa.me/55${String(debitmanagmentItem.renter.whatsapp).replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {debitmanagmentItem.renter.whatsapp}
                      </a> : 'N/A'
                  },
                  {
                    label: "Valor em Aberto", value: debitmanagmentItem?.amount
                      ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(debitmanagmentItem.amount))
                      : 'N/A'
                  },
                  { label: "Atraso (em dias)", value: debitmanagmentItem?.daysOverdue || 'N/A' },
                  {
                    label: "Veículo de Parceiro",
                    value: debitmanagmentItem?.renter?.partnerId && !namePartner?.toUpperCase().includes("LOCAVIBE")
                      ? <span style={{ color: '#e53e3e', fontWeight: 'bold' }}>Sim</span>
                      : <span style={{ color: '#38a169', fontWeight: 'bold' }}>Não</span>
                  },
                ].concat(namePartner ? [{ label: "Proprietário", value: namePartner }] : [])}
              />

              <GenericTable
                entity="negotiations"
                headers={HEADERS_NEGOTIATIONS}
                renders={columnPayments as never}
                data={debitmanagmentItem?.negotiations?.flatMap(n => n.paymentNegotiations) || []}
              />

              <br></br>
              <GenericTable
                entity="callInteractions"
                headers={HEADERS_CALLINTERACTION}
                renders={columnCallInteractions as never}

                data={callInteractions || []}
              />

              <section
                className="mt-5 mb-10 p-3 rounded-md shadow"
                style={{ backgroundColor: 'rgba(97, 97, 97, 0.2)' }}
              >
                <h2 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2 flex items-center">
                  <span className="mr-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
                    </svg>
                  </span>
                  Registro de Contato
                </h2>

                <Formik
                  initialValues={{ birthDate: '', notes: '' }}
                  onSubmit={() => { }}
                  innerRef={formikRef}
                >
                  <Form className='m-2'>
                    <InputFormik
                      label="Data de Interação com o Cliente"
                      name="birthDate"
                      type="date"
                      required={true}
                    />

                    <div className='mt-4'></div>
                    <label htmlFor="notes" className="block mb-2 font-medium text-gray-700">
                      Observações <span style={{ color: 'redPrimary' }}>*</span>
                    </label>
                    <div className="mt-1">
                      <Field
                        as="textarea"
                        id="notes"
                        name="notes"
                        rows={4}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2"
                        placeholder="Digite suas observações aqui..."
                        required={true}
                      />
                    </div>
                    <div className="mt-4 text-right">
                      <ButtonWithLoader
                        type="button"
                        isLoading={bindVelotrackInProgress}
                        onClick={handleContactRegistration}
                      >
                        Registrar Interação
                      </ButtonWithLoader>
                    </div>
                  </Form>
                </Formik>
              </section>

            </div>
            <div className="mt-4 text-right mb-2 sticky bottom-0 bg-white pt-2">
              <button
                onClick={handleClose}
                className="px-3 py-1 text-white rounded"
                style={{ backgroundColor: 'rgb(228, 65, 49)' }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentArrangementModal;